import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarPlus, Download } from 'lucide-react'
import apiClient, { ApiClientError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toastHelpers } from '@/lib/toast-helpers'
import { dayLabel, downloadCSV, leaveTypeLabel, todayISO } from '@/lib/hr'
import { cn } from '@/lib/utils'
import type { HRLeaveBalances, HRLeaveEntry, HRLeaveType, HRYearEndRow } from '@/types/hr'
import { Banner, Empty, Field, NativeSelect, Segmented, errMsg } from './shared'
import { useHREmployees } from './PeopleTab'
import { hrRole } from './HRPage'

// Leave (F-HR-N3): what everyone has left, recording leave for a range of
// days, and the admin's year-end close (carry over / pay out / lapse, by the
// rules set on each leave type).

export function LeaveTab() {
  const { isAdmin, canMark } = hrRole()
  const [view, setView] = useState<'balances' | 'taken' | 'year-end'>('balances')
  const [recording, setRecording] = useState(false)
  const [adjusting, setAdjusting] = useState(false)
  const bq = useQuery({ queryKey: ['hr', 'leave-balances'], queryFn: () => apiClient.hrLeaveBalances() })
  const bal = bq.data?.data as HRLeaveBalances | undefined

  const exportCSV = () => {
    if (!bal) return
    downloadCSV(`leave-balances-${bal.as_of}.csv`, [
      ['Name', 'Department', 'Leave year', ...bal.types.flatMap((t) => [`${t.code} entitled`, `${t.code} taken`, `${t.code} left`])],
      ...bal.people.map((p) => [
        p.full_name,
        p.department,
        `${p.year_start} – ${p.year_end}`,
        ...bal.types.flatMap((t) => {
          const b = p.balances.find((x) => x.leave_type_id === t.id)
          return (b ? [b.entitled + b.carried - b.carried_lapsed + b.adjusted, b.used, b.available] : ['', '', '']) as (string | number)[]
        }),
      ]),
    ])
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { id: 'balances', label: 'Balances' },
            { id: 'taken', label: 'Leave taken' },
            ...(isAdmin ? [{ id: 'year-end' as const, label: 'Year end' }] : []),
          ]}
        />
        <div className="flex flex-wrap gap-2">
          {view === 'balances' && (
            <Button variant="outline" size="sm" onClick={exportCSV} disabled={!bal?.people.length}>
              <Download className="mr-1.5 h-4 w-4" />
              Export
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setAdjusting(true)}>
              Adjust a balance
            </Button>
          )}
          {canMark && (
            <Button size="sm" onClick={() => setRecording(true)}>
              <CalendarPlus className="mr-1.5 h-4 w-4" />
              Record leave
            </Button>
          )}
        </div>
      </div>

      {view === 'balances' &&
        (!bal ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : bal.people.length === 0 ? (
          <Empty>No one on Staff &amp; Payroll yet.</Empty>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 font-medium">Name</th>
                  {bal.types.map((t) => (
                    <th key={t.id} className="px-4 py-2 text-right font-medium" title={`${t.yearly_quota} days a year${t.paid ? '' : ', unpaid'}`}>
                      {t.name} left
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {bal.people.map((p) => (
                  <tr key={p.employee_id}>
                    <td className="px-4 py-2">
                      <div className="font-medium">{p.full_name}</div>
                      <div className="text-xs text-muted-foreground">{p.department}</div>
                    </td>
                    {bal.types.map((t) => {
                      const b = p.balances.find((x) => x.leave_type_id === t.id)
                      if (!b) return <td key={t.id} />
                      const total = b.entitled + b.carried - b.carried_lapsed + b.adjusted
                      return (
                        <td key={t.id} className="px-4 py-2 text-right tabular-nums" title={`${total} available this year, ${b.used} taken`}>
                          {t.yearly_quota > 0 || total > 0 ? (
                            <>
                              <span className={cn('font-medium', b.available < 0 && 'text-red-700 dark:text-red-300')}>{b.available}</span>
                              <span className="text-muted-foreground"> / {total}</span>
                            </>
                          ) : (
                            <span className="text-muted-foreground">{b.used ? `${b.used} taken` : '—'}</span>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

      {view === 'taken' && <LeaveTaken />}
      {view === 'year-end' && isAdmin && <YearEnd />}

      <RecordLeaveDialog open={recording} onClose={() => setRecording(false)} />
      {isAdmin && <AdjustDialog open={adjusting} onClose={() => setAdjusting(false)} />}
    </div>
  )
}

function LeaveTaken() {
  const qc = useQueryClient()
  const { canMark } = hrRole()
  const q = useQuery({ queryKey: ['hr', 'leave-entries', 'all'], queryFn: () => apiClient.hrListLeaveEntries() })
  const entries = (q.data?.data as HRLeaveEntry[] | undefined) ?? []
  const cancel = useMutation({
    mutationFn: (id: string) => apiClient.hrCancelLeave(id),
    onSuccess: () => {
      toastHelpers.success('Leave cancelled', 'Those days are unmarked again')
      qc.invalidateQueries({ queryKey: ['hr'] })
    },
    onError: (err) => toastHelpers.error('Couldn’t cancel', errMsg(err)),
  })
  if (q.isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>
  if (!entries.length) return <Empty>No leave recorded yet. Leave marked straight on the register counts too.</Empty>
  return (
    <div className="divide-y rounded-lg border text-sm">
      {entries.map((x) => (
        <div key={x.id} className={cn('flex flex-wrap items-center justify-between gap-2 px-4 py-2.5', x.cancelled && 'opacity-50')}>
          <div>
            <div className="font-medium">
              {x.full_name} · {x.leave_type}
            </div>
            <div className="text-xs text-muted-foreground">
              {x.from === x.to ? dayLabel(x.from, true) : `${dayLabel(x.from)} – ${dayLabel(x.to, true)}`}
              {x.half_day ? ' · half day' : ''} · {x.cancelled ? 'cancelled' : `${x.days} ${x.days === 1 ? 'day' : 'days'}`}
              {x.recorded_by ? ` · by ${x.recorded_by}` : ''}
              {x.note ? ` · ${x.note}` : ''}
            </div>
          </div>
          {canMark && !x.cancelled && (
            <Button variant="ghost" size="sm" onClick={() => cancel.mutate(x.id)} disabled={cancel.isPending}>
              Cancel leave
            </Button>
          )}
        </div>
      ))}
    </div>
  )
}

function RecordLeaveDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const { people } = useHREmployees('active')
  const typesQ = useQuery({ queryKey: ['hr', 'leave-types'], queryFn: () => apiClient.hrListLeaveTypes(), enabled: open })
  const types = (typesQ.data?.data as HRLeaveType[] | undefined) ?? []
  const [emp, setEmp] = useState('')
  const [type, setType] = useState('')
  const [from, setFrom] = useState(todayISO())
  const [to, setTo] = useState(todayISO())
  const [half, setHalf] = useState(false)
  const [note, setNote] = useState('')
  const [over, setOver] = useState<string | null>(null)
  useEffect(() => {
    if (open) {
      setEmp('')
      setType('')
      setFrom(todayISO())
      setTo(todayISO())
      setHalf(false)
      setNote('')
      setOver(null)
    }
  }, [open])
  const save = useMutation({
    mutationFn: (allowOver: boolean) =>
      apiClient.hrRecordLeave({ employee_id: emp, leave_type_id: type, from, to: half ? from : to, half_day: half, note, allow_over: allowOver }),
    onSuccess: (res) => {
      toastHelpers.success('Leave recorded', `${res.data?.days ?? ''} days marked on the register`)
      qc.invalidateQueries({ queryKey: ['hr'] })
      onClose()
    },
    onError: (err) => {
      if ((err as ApiClientError).code === 'hr_leave_over_balance') setOver(errMsg(err))
      else toastHelpers.error('Couldn’t record leave', errMsg(err))
    },
  })
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Record leave</DialogTitle>
          <DialogDescription>Every working day in the range is marked as leave. Weekly offs and holidays inside it don’t use up leave.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Person">
            <NativeSelect value={emp} onChange={(e) => setEmp(e.target.value)}>
              <option value="">Choose…</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.full_name}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label="Leave">
            <NativeSelect value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">Choose…</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {leaveTypeLabel(t)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label={half ? 'Date' : 'From'}>
            <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); if (to < e.target.value) setTo(e.target.value) }} />
          </Field>
          {!half && (
            <Field label="To">
              <Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
            </Field>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={half} onChange={(e) => setHalf(e.target.checked)} />
          Half day (worked the other half)
        </label>
        <Field label="Note">
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {over && (
          <Banner tone="warn">
            {over}. Record it anyway? It will show as a negative balance.
          </Banner>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {over ? (
            <Button variant="destructive" onClick={() => save.mutate(true)} disabled={save.isPending}>
              Record anyway
            </Button>
          ) : (
            <Button onClick={() => save.mutate(false)} disabled={!emp || !type || !from || save.isPending}>
              Record leave
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function AdjustDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const { people } = useHREmployees('active')
  const typesQ = useQuery({ queryKey: ['hr', 'leave-types'], queryFn: () => apiClient.hrListLeaveTypes(), enabled: open })
  const types = (typesQ.data?.data as HRLeaveType[] | undefined) ?? []
  const [emp, setEmp] = useState('')
  const [type, setType] = useState('')
  const [daysV, setDays] = useState('')
  const [reason, setReason] = useState('')
  useEffect(() => {
    if (open) {
      setEmp('')
      setType('')
      setDays('')
      setReason('')
    }
  }, [open])
  const save = useMutation({
    mutationFn: () => apiClient.hrAdjustLeave({ employee_id: emp, leave_type_id: type, days: Number(daysV), reason }),
    onSuccess: () => {
      toastHelpers.success('Balance adjusted')
      qc.invalidateQueries({ queryKey: ['hr'] })
      onClose()
    },
    onError: (err) => toastHelpers.error('Couldn’t adjust', errMsg(err)),
  })
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Adjust a leave balance</DialogTitle>
          <DialogDescription>Add days (e.g. 2) or take them away (e.g. -1) for this leave year. Kept with your reason.</DialogDescription>
        </DialogHeader>
        <Field label="Person">
          <NativeSelect value={emp} onChange={(e) => setEmp(e.target.value)}>
            <option value="">Choose…</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.full_name}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Leave">
            <NativeSelect value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">Choose…</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label="Days">
            <Input type="number" step={0.5} value={daysV} onChange={(e) => setDays(e.target.value)} />
          </Field>
        </div>
        <Field label="Reason">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={!emp || !type || !Number(daysV) || !reason.trim() || save.isPending}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function YearEnd() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['hr', 'year-end'], queryFn: () => apiClient.hrPreviewYearEnd() })
  const plan = (q.data?.data as HRYearEndRow[] | undefined) ?? []
  const close = useMutation({
    mutationFn: () => apiClient.hrCloseYearEnd(),
    onSuccess: () => {
      toastHelpers.success('Leave year closed', 'Carried days are on the new year; paid-out days will show on the next payroll')
      qc.invalidateQueries({ queryKey: ['hr'] })
    },
    onError: (err) => toastHelpers.error('Couldn’t close the year', errMsg(err)),
  })
  const meaningful = plan.filter((r) => r.unused > 0)
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        When a leave year ends, each type’s unused days are carried over, paid out or lapse — as set in Setup → Leave types. Review, then close.
      </p>
      {q.isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : plan.length === 0 ? (
        <Empty>No leave year is waiting to be closed.</Empty>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                <tr>
                  {['Name', 'Leave', 'Year', 'Unused', 'Carry over', 'Pay out', 'Lapse'].map((h) => (
                    <th key={h} className="px-4 py-2 font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {(meaningful.length ? meaningful : plan).map((r) => (
                  <tr key={`${r.employee_id}-${r.leave_type_id}`}>
                    <td className="px-4 py-2 font-medium">{r.full_name}</td>
                    <td className="px-4 py-2">{r.leave_type}</td>
                    <td className="px-4 py-2 tabular-nums text-muted-foreground">
                      {r.year_start} – {r.year_end}
                    </td>
                    <td className="px-4 py-2 tabular-nums">{r.unused}</td>
                    <td className="px-4 py-2 tabular-nums">{r.carry || '—'}</td>
                    <td className="px-4 py-2 tabular-nums">{r.encash || '—'}</td>
                    <td className="px-4 py-2 tabular-nums">{r.lapse || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end">
            <Button onClick={() => close.mutate()} disabled={close.isPending}>
              Close the leave year
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
